/**
 * Cookie Storage Utility
 * Handles storing and retrieving previous metal rates for price change tracking
 */

const COOKIE_NAME = 'metal_rates_previous';
const COOKIE_EXPIRY_HOURS = 24;

export interface StoredRates {
  gold: number;
  silver: number;
  timestamp: string; // ISO string
}

/**
 * Saves previous metal rates to cookies with 24-hour expiry
 * 
 * @param gold - Gold rate per gram in INR
 * @param silver - Silver rate per gram in INR
 * @param timestamp - Date when rates were fetched
 */
export function savePreviousRates(gold: number, silver: number, timestamp: Date): void {
  const data: StoredRates = {
    gold,
    silver,
    timestamp: timestamp.toISOString(),
  };

  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + COOKIE_EXPIRY_HOURS);

  document.cookie = `${COOKIE_NAME}=${JSON.stringify(data)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;
}

/**
 * Retrieves previous metal rates from cookies
 * 
 * @returns StoredRates object or null if not found or expired
 */
export function getPreviousRates(): StoredRates | null {
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    
    if (name === COOKIE_NAME) {
      try {
        const data: StoredRates = JSON.parse(decodeURIComponent(value));
        
        // Validate data structure
        if (
          typeof data.gold === 'number' &&
          typeof data.silver === 'number' &&
          typeof data.timestamp === 'string'
        ) {
          return data;
        }
      } catch (error) {
        console.error('Error parsing stored rates:', error);
        return null;
      }
    }
  }
  
  return null;
}

/**
 * Clears stored rates from cookies
 */
export function clearPreviousRates(): void {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}
